import React from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchSummary } from '../../../store/slices/headOfficeFinanceSlice';
import { LedgerSummaryItem } from '../../../services/headOfficeFinanceApi';

const formatCurrency = (value: number) =>
    `Rs. ${value.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const HeadOfficeSummaryTab: React.FC = () => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { summary, summaryLoading, error } = useAppSelector(
        (state) => state.headOfficeFinance
    );

    React.useEffect(() => {
        dispatch(fetchSummary());
    }, [dispatch]);

    const handleRefresh = () => {
        dispatch(fetchSummary());
    };

    const totalBalance = summary.reduce((acc, item) => acc + item.balance, 0);
    const totalDebit = summary.reduce((acc, item) => acc + item.total_debit, 0);
    const totalCredit = summary.reduce((acc, item) => acc + item.total_credit, 0);

    const renderSummaryCard = (item: LedgerSummaryItem) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.type}>
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {item.name}
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        {formatCurrency(item.balance)}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Total Debit
                            </Typography>
                            <Typography variant="body2" color="error.main">
                                {formatCurrency(item.total_debit)}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                                Total Credit
                            </Typography>
                            <Typography variant="body2" color="success.main">
                                {formatCurrency(item.total_credit)}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
    );

    const renderSummaryRow = (item: LedgerSummaryItem) => (
        <TableRow key={item.type}>
            <TableCell>{item.name}</TableCell>
            <TableCell align="right">{formatCurrency(item.total_debit)}</TableCell>
            <TableCell align="right">{formatCurrency(item.total_credit)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatCurrency(item.balance)}
            </TableCell>
        </TableRow>
    );

    return (
        <Card>
            <CardContent>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                    }}
                >
                    <Typography variant="h6">Ledger Summary</Typography>
                    <IconButton onClick={handleRefresh} disabled={summaryLoading}>
                        <RefreshIcon />
                    </IconButton>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {summaryLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!summaryLoading && summary.length === 0 && (
                    <Alert severity="info">No summary data available.</Alert>
                )}

                {!summaryLoading && summary.length > 0 && (
                    <Stack spacing={4}>
                        {/* Summary Cards */}
                        <Grid container spacing={2}>
                            {summary.map(renderSummaryCard)}
                        </Grid>

                        {/* Totals Card */}
                        <Card
                            variant="outlined"
                            sx={{ bgcolor: 'action.hover' }}
                        >
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Combined Total Debit
                                        </Typography>
                                        <Typography variant="h5" color="error">
                                            {formatCurrency(totalDebit)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Combined Total Credit
                                        </Typography>
                                        <Typography variant="h5" color="success.main">
                                            {formatCurrency(totalCredit)}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Net Balance
                                        </Typography>
                                        <Typography variant="h5">
                                            {formatCurrency(totalBalance)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Table View (non-mobile) */}
                        {!isMobile && (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Ledger Type</TableCell>
                                            <TableCell align="right">Total Debit</TableCell>
                                            <TableCell align="right">Total Credit</TableCell>
                                            <TableCell align="right">Balance</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {summary.map(renderSummaryRow)}
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                Grand Total
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(totalDebit)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(totalCredit)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(totalBalance)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};

export default HeadOfficeSummaryTab;
